import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { fmtHours } from "@/lib/utils"
import { router } from "@inertiajs/react"
import { IconCopy, IconDots, IconPencil, IconTrash } from "@tabler/icons-react"
import { useCallback, useRef } from "react"

const HOUR_HEIGHT = 60

const COLORS = [
  { border: "border-l-blue-500", bg: "bg-blue-50 dark:bg-blue-950/40", solid: "bg-blue-500" },
  { border: "border-l-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-950/40", solid: "bg-emerald-500" },
  { border: "border-l-violet-500", bg: "bg-violet-50 dark:bg-violet-950/40", solid: "bg-violet-500" },
  { border: "border-l-amber-500", bg: "bg-amber-50 dark:bg-amber-950/40", solid: "bg-amber-500" },
  { border: "border-l-rose-500", bg: "bg-rose-50 dark:bg-rose-950/40", solid: "bg-rose-500" },
  { border: "border-l-cyan-500", bg: "bg-cyan-50 dark:bg-cyan-950/40", solid: "bg-cyan-500" },
  { border: "border-l-orange-500", bg: "bg-orange-50 dark:bg-orange-950/40", solid: "bg-orange-500" },
  { border: "border-l-pink-500", bg: "bg-pink-50 dark:bg-pink-950/40", solid: "bg-pink-500" },
  { border: "border-l-teal-500", bg: "bg-teal-50 dark:bg-teal-950/40", solid: "bg-teal-500" },
  { border: "border-l-indigo-500", bg: "bg-indigo-50 dark:bg-indigo-950/40", solid: "bg-indigo-500" },
]

export function getEntryColor(projectId: number) {
  return COLORS[projectId % COLORS.length]
}

interface TimeEntry {
  id: number
  project_id: number | null
  project_name: string
  customer_name: string
  description: string
  user_name?: string
  hours: number | null
  started_at: string | null
  stopped_at: string | null
  billable: boolean
  running: boolean
}

interface Props {
  entry: TimeEntry
  top: number
  height: number
  calendarDate: string
  dayIndex: number
  totalDays: number
  timeLabelWidth: number
  columnIndex: number
  totalColumns: number
  faded?: boolean
  isAdmin?: boolean
  onDragStart?: (offsetMin: number) => void
  onResizeStart?: (edge: "top" | "bottom") => void
  onEdit?: (entry: TimeEntry) => void
  displayTimeRange?: string
  displayDuration?: string
}

function formatTimeRange(startedAt: string | null, stoppedAt: string | null) {
  if (!startedAt) return ""
  const start = new Date(startedAt)
  const end = stoppedAt ? new Date(stoppedAt) : new Date()
  const fmt = (d: Date) => d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })
  return `${fmt(start)} – ${fmt(end)}`
}

function formatDuration(hours: number | null) {
  if (hours === null) return ""
  return fmtHours(hours)
}

export default function CalendarEntryBlock({
  entry,
  top,
  height,
  calendarDate,
  dayIndex,
  totalDays,
  timeLabelWidth,
  columnIndex,
  totalColumns,
  faded,
  isAdmin,
  onDragStart,
  onResizeStart,
  onEdit,
  displayTimeRange,
  displayDuration,
}: Props) {
  const color = getEntryColor(entry.project_id || 0)
  const isSmall = height < 40
  const isTiny = height < 24

  const didDragRef = useRef(false)
  const mouseDownPosRef = useRef({ x: 0, y: 0 })

  function handleDelete(e: React.MouseEvent) {
    e.stopPropagation()
    if (confirm("Delete this time entry?")) {
      router.delete(`/time_entries/${entry.id}`, {
        data: { view: "calendar", date: calendarDate },
      })
    }
  }

  function handleDuplicate(e: React.MouseEvent) {
    e.stopPropagation()
    router.post(`/time_entries/${entry.id}/duplicate`, {
      view: "calendar",
      date: calendarDate,
    })
  }

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return
      const target = e.target as HTMLElement
      if (target.closest("[data-dropdown-trigger]")) return
      if (target.closest("button")) return
      if (target.closest("[data-resize-handle]")) return

      mouseDownPosRef.current = { x: e.clientX, y: e.clientY }
      didDragRef.current = false

      const entryBlockEl = e.currentTarget as HTMLElement

      const handleMove = (moveE: MouseEvent) => {
        const dx = moveE.clientX - mouseDownPosRef.current.x
        const dy = moveE.clientY - mouseDownPosRef.current.y
        if (Math.abs(dx) > 4 || Math.abs(dy) > 4) {
          if (!didDragRef.current) {
            didDragRef.current = true
            const offsetPx = mouseDownPosRef.current.y - entryBlockEl.getBoundingClientRect().top
            const offsetMin = Math.round((offsetPx / HOUR_HEIGHT) * 60)
            onDragStart?.(offsetMin)
          }
        }
      }

      const handleUp = () => {
        document.removeEventListener("mousemove", handleMove)
        document.removeEventListener("mouseup", handleUp)
        if (!didDragRef.current) {
          onEdit?.(entry)
        }
      }

      document.addEventListener("mousemove", handleMove)
      document.addEventListener("mouseup", handleUp)

      e.preventDefault()
    },
    [onDragStart, onEdit, entry],
  )

  const handleResizeMouseDown = useCallback(
    (edge: "top" | "bottom") => (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      onResizeStart?.(edge)
    },
    [onResizeStart],
  )

  // Compute position within the day column, splitting for overlaps
  const dayColWidth = `(${100 / totalDays}% - ${timeLabelWidth / totalDays}px - 4px)`
  const dayColLeft = `(${timeLabelWidth}px + ${(dayIndex / totalDays) * 100}% - ${(dayIndex / totalDays) * timeLabelWidth}px + 2px)`
  const subColWidth = `calc(${dayColWidth} / ${totalColumns})`
  const colLeft = `calc(${dayColLeft} + ${dayColWidth} * ${columnIndex} / ${totalColumns})`

  return (
    <div
      data-entry-block
      className={`absolute border-l-3 rounded-md px-2 py-0.5 cursor-grab overflow-hidden group hover:shadow-md transition-shadow ${color.border} ${color.bg} ${faded ? "opacity-30" : ""}`}
      style={{
        top: `${top}px`,
        height: `${Math.max(height, 18)}px`,
        left: colLeft,
        width: subColWidth,
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Top resize handle */}
      <div
        data-resize-handle
        className="absolute top-0 left-0 right-0 h-1.5 cursor-ns-resize z-10 opacity-0 group-hover:opacity-100 hover:bg-foreground/10 rounded-t-md"
        onMouseDown={handleResizeMouseDown("top")}
      />

      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1 overflow-hidden">
          {isTiny ? (
            <p className="truncate text-[11px] font-medium leading-tight">
              {isAdmin && entry.user_name ? `${entry.user_name} · ` : ""}
              {entry.project_name} — {entry.description}
            </p>
          ) : isSmall ? (
            <>
              <p className="truncate text-xs font-medium leading-tight">{entry.project_name}</p>
              {isAdmin && entry.user_name && (
                <p className="truncate text-[11px] font-semibold leading-tight">{entry.user_name}</p>
              )}
              <p className="text-muted-foreground truncate text-[11px] leading-tight">{entry.description}</p>
            </>
          ) : (
            <>
              <p className="truncate text-xs font-medium leading-tight">{entry.project_name}</p>
              {isAdmin && entry.user_name && (
                <p className="truncate text-[11px] font-semibold leading-tight">{entry.user_name}</p>
              )}
              <p className="text-muted-foreground truncate text-[11px] leading-tight">{entry.description}</p>
              <p className="text-muted-foreground text-[11px] leading-tight">
                {displayTimeRange || formatTimeRange(entry.started_at, entry.stopped_at)}
                {` (${displayDuration || formatDuration(entry.hours)})`}
              </p>
            </>
          )}
        </div>
        <div
          className="opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
          data-dropdown-trigger
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-5 w-5">
                <IconDots className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onMouseDown={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => onEdit?.(entry)}>
                <IconPencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDuplicate}>
                <IconCopy className="mr-2 h-4 w-4" />
                Duplicate
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onClick={handleDelete}>
                <IconTrash className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Bottom resize handle */}
      <div
        data-resize-handle
        className="absolute bottom-0 left-0 right-0 h-1.5 cursor-ns-resize z-10 opacity-0 group-hover:opacity-100 hover:bg-foreground/10 rounded-b-md"
        onMouseDown={handleResizeMouseDown("bottom")}
      />
    </div>
  )
}
